import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import { journalAssetPurchaseCash, journalAssetPurchasePayable, journalSupplierPayment } from '../lib/journalEngine'

const STATUS_COLORS = {
  'Active': 'badge-green',
  'CWIP': 'badge-amber',
  'Prepayment': 'badge-blue',
  'Disposed': 'badge-grey',
  'Fully Depreciated': 'badge-grey',
}

const CATEGORIES = [
  'Greenhouse Structure',
  'Hydroponic Systems',
  'Solar & Electrical',
  'Vehicles',
  'Office Equipment',
  'Other',
]

export default function FixedAssets() {
  const { user, profile, isAdmin } = useAuth()
  const [assets, setAssets] = useState([])
  const [schedule, setSchedule] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('register')
  const [msg, setMsg] = useState(null)
  const [editAsset, setEditAsset] = useState(null)
  const [payModal, setPayModal] = useState(null)
  const [payForm, setPayForm] = useState({ amount: '', payment_date: ugandaDate(), reference: '', notes: '' })
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [assetRes, schedRes] = await Promise.all([
      supabase.from('fixed_assets').select('*').order('asset_number'),
      supabase.from('depreciation_schedule').select('*'),
    ])
    setAssets(assetRes.data || [])
    setSchedule(schedRes.data || [])
    setLoading(false)
  }

  const totalCost = assets.reduce((s, a) => s + (a.total_cost || 0), 0)
  const totalPaid = assets.reduce((s, a) => s + (a.amount_paid || 0), 0)
  const totalOutstanding = assets.reduce((s, a) => s + (a.amount_outstanding || 0), 0)
  const totalMonthlyDep = assets.filter(a => a.status === 'Active').reduce((s, a) => s + (a.monthly_depreciation || 0), 0)
  const totalAccumDep = schedule.reduce((s, a) => s + (parseFloat(a.accumulated_depreciation) || 0), 0)
  const totalNBV = schedule.reduce((s, a) => s + (parseFloat(a.net_book_value) || 0), 0)

  const handleUpload = async (assetId, field, file) => {
    if (!file) return
    setUploading(true)
    const fileName = `assets/${assetId}-${field}-${Date.now()}-${file.name}`
    const { data, error } = await supabase.storage.from('attachments').upload(fileName, file)
    if (!error) {
      const { data: urlData } = supabase.storage.from('attachments').getPublicUrl(fileName)
      await supabase.from('fixed_assets').update({ [field]: urlData.publicUrl }).eq('id', assetId)
      setMsg({ type: 'success', text: 'Document uploaded successfully.' })
      loadData()
    } else {
      setMsg({ type: 'error', text: 'Upload failed: ' + error.message })
    }
    setUploading(false)
    setTimeout(() => setMsg(null), 3000)
  }

  const handleSignOff = async (assetId, person) => {
    const updates = person === 'sharon'
      ? { sharon_confirmed: true, sharon_confirmed_at: new Date().toISOString() }
      : { edward_authorised: true, edward_authorised_at: new Date().toISOString() }
    await supabase.from('fixed_assets').update(updates).eq('id', assetId)
    loadData()
    setMsg({ type: 'success', text: `${person === 'sharon' ? 'Sharon' : 'Edward'} sign-off recorded.` })
    setTimeout(() => setMsg(null), 3000)
  }

  const [commissionModal, setCommissionModal] = React.useState(null)
  const [commissionDate, setCommissionDate] = React.useState('')

  const handleCommission = (assetId) => {
    setCommissionModal(assetId)
    setCommissionDate(ugandaDate())
  }

  const handleConfirmCommission = async () => {
    // Get asset details before updating
    const { data: asset } = await supabase.from('fixed_assets').select('*').eq('id', commissionModal).single()

    await supabase.from('fixed_assets').update({
      status: 'Active', commissioning_date: commissionDate, updated_at: new Date().toISOString()
    }).eq('id', commissionModal)

    // Journal entry: Dr Fixed Asset account / Cr 1610 CWIP
    // Map category to account code
    const categoryMap = {
      'Greenhouse Structure': '1601',
      'Hydroponic Systems': '1602',
      'Solar & Electrical': '1603',
      'Equipment & Machinery': '1604',
    }
    const assetAccount = categoryMap[asset?.category] || '1604'
    const assetValue = asset?.total_cost || 0

    if (assetValue > 0) {
      await supabase.from('journal_entries').insert({
        journal_id: `JNL-FA-CAP-${commissionModal}`,
        entry_date: commissionDate,
        module: 'fixed_asset_capitalise',
        reference: asset?.name,
        debit_account: assetAccount,
        credit_account: '1610',
        amount: assetValue,
        description: `Asset capitalised — ${asset?.name}`,
        source_id: commissionModal,
        created_by: user.id,
      })
    }

    setCommissionModal(null); loadData()
    setMsg({ type: 'success', text: `Asset commissioned and capitalised. UGX ${assetValue.toLocaleString()} transferred from CWIP to Fixed Assets. Depreciation begins from ${commissionDate}.` })
    setTimeout(() => setMsg(null), 5000)
  }

  const handleSaveAsset = async () => {
    setSaving(true)
    const { id, monthly_depreciation, ...data } = editAsset
    const payload = {
      ...data,
      amount_outstanding: (data.total_cost || 0) - (data.amount_paid || 0),
      updated_at: new Date().toISOString(),
      created_by: user.id
    }

    if (id) {
      // EDIT — detect if amount_paid increased (new payment made)
      const { data: existing } = await supabase.from('fixed_assets').select('amount_paid, name').eq('id', id).single()
      const prevPaid = parseFloat(existing?.amount_paid || 0)
      const newPaid = parseFloat(data.amount_paid || 0)
      const additionalPayment = newPaid - prevPaid

      const { error } = await supabase.from('fixed_assets').update(payload).eq('id', id)
      if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }

      if (additionalPayment > 0) {
        const payDate = ugandaDate()
        // Dr 2101 Supplier Payables / Cr 1101 Cash (clearing the payable with cash)
        await supabase.from('journal_entries').insert({
          journal_id: `JNL-FA-PAY-${id}-${Date.now()}`,
          entry_date: payDate,
          module: 'fixed_asset_payment',
          reference: existing?.name || data.name,
          debit_account: '2101',
          credit_account: '1101',
          amount: additionalPayment,
          description: `Additional payment — ${existing?.name || data.name}`,
          source_id: id,
          created_by: user.id,
        })
        // Cash log (cash out)
        await supabase.from('cash_logs').insert({
          user_id: user.id,
          log_date: payDate,
          total_income: 0,
          total_expenses: additionalPayment,
          net: -additionalPayment,
          income_rows: [],
          expense_rows: [{ type: 'invoice', description: `Asset payment — ${existing?.name || data.name}`, amount: additionalPayment }],
          notes: `Additional payment on fixed asset: ${existing?.name || data.name}`,
        })
        setMsg({ type: 'success', text: `Asset updated. Payment of UGX ${additionalPayment.toLocaleString()} recorded to cash and journals.` })
      } else {
        setMsg({ type: 'success', text: 'Asset updated.' })
      }
    } else {
      // NEW asset
      const { error } = await supabase.from('fixed_assets').insert(payload)
      if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }

      const { data: newAsset } = await supabase.from('fixed_assets').select('id').eq('name', data.name).order('created_at', { ascending: false }).limit(1).single()
      if (newAsset) {
        const assetDate = data.purchase_date || ugandaDate()
        if (data.amount_paid > 0) {
          await journalAssetPurchaseCash({
            assetId: newAsset.id, assetName: data.name,
            amount: data.amount_paid, date: assetDate, userId: user.id,
          })
        }
        if (payload.amount_outstanding > 0) {
          await journalAssetPurchasePayable({
            assetId: newAsset.id, assetName: data.name,
            amount: payload.amount_outstanding, date: assetDate, userId: user.id,
          })
        }
      }
      setMsg({ type: 'success', text: 'Asset saved.' })
    }

    setEditAsset(null); loadData()
    setSaving(false)
    setTimeout(() => setMsg(null), 4000)
  }

  const handleRecordPayment = async () => {
    if (!payForm.amount || !payModal) return
    const amount = parseFloat(payForm.amount)
    if (amount <= 0 || amount > payModal.amount_outstanding) {
      setMsg({ type: 'error', text: `Amount must be between 1 and UGX ${payModal.amount_outstanding.toLocaleString()}` })
      return
    }
    setSaving(true)
    const newPaid = (payModal.amount_paid || 0) + amount
    const newOutstanding = (payModal.total_cost || 0) - newPaid

    // 1. Update fixed asset record
    await supabase.from('fixed_assets').update({
      amount_paid: newPaid,
      amount_outstanding: Math.max(0, newOutstanding),
      updated_at: new Date().toISOString(),
    }).eq('id', payModal.id)

    // 2. Create cash log entry (cash out)
    await supabase.from('cash_logs').insert({
      user_id: user.id,
      log_date: payForm.payment_date,
      total_income: 0,
      total_expenses: amount,
      net: -amount,
      income_rows: [],
      expense_rows: [{ type: 'invoice', description: `Asset payment — ${payModal.name}`, amount }],
      notes: `Payment for ${payModal.name}. Ref: ${payForm.reference || '—'}. ${payForm.notes || ''}`,
    })

    // 3. Journal entry: Dr 2101 Supplier Payables / Cr 1101 Cash
    await journalSupplierPayment({
      paymentId: `${payModal.id}-${Date.now()}`,
      supplier: payModal.supplier || payModal.name,
      amount,
      date: payForm.payment_date,
      userId: user.id,
    })

    setSaving(false)
    setPayModal(null)
    setPayForm({ amount: '', payment_date: ugandaDate(), reference: '', notes: '' })
    setMsg({ type: 'success', text: `Payment of UGX ${amount.toLocaleString()} recorded. Cash and payables updated.` })
    loadData()
    setTimeout(() => setMsg(null), 4000)
  }
  const isEdwardProfile = profile?.email?.includes('elkatongole')
  const isSharonProfile = profile?.email?.includes('snankunda')

  return (
    <AppLayout title="Fixed Assets">
      <div className="section-header">
        <div className="section-icon">🏗️</div>
        <div className="section-info">
          <h2>Fixed Assets Register</h2>
          <p>Track all capital assets, depreciation, and outstanding payments. Sharon confirms receipt, Edward authorises capitalisation.</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }}
            onClick={() => setEditAsset({
              name: '', description: '', category: 'Hydroponic Systems',
              account_code: '1040', depreciation_account: '7001',
              supplier: '', invoice_number: '', total_cost: '',
              amount_paid: '', purchase_date: ugandaDate(),
              status: 'CWIP', useful_life_years: 7,
              sharon_confirmed: false, edward_authorised: false, notes: ''
            })}>
            + Add Asset
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 18 }}>
        <div className="kpi"><div className="kpi-label">Total Asset Cost</div><div className="kpi-value" style={{ fontSize: '1.2rem' }}>UGX {totalCost.toLocaleString()}</div></div>
        <div className="kpi green"><div className="kpi-label">Net Book Value</div><div className="kpi-value" style={{ fontSize: '1.2rem' }}>UGX {Math.round(totalNBV).toLocaleString()}</div></div>
        <div className="kpi"><div className="kpi-label">Monthly Depreciation</div><div className="kpi-value" style={{ fontSize: '1.1rem' }}>UGX {Math.round(totalMonthlyDep).toLocaleString()}</div></div>
        <div className={`kpi ${totalOutstanding > 0 ? 'red' : ''}`}><div className="kpi-label">Outstanding Payments</div><div className="kpi-value" style={{ fontSize: '1.1rem' }}>UGX {totalOutstanding.toLocaleString()}</div></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'register' ? 'active' : ''}`} onClick={() => setTab('register')}>📋 Asset Register</button>
        <button className={`tab ${tab === 'schedule' ? 'active' : ''}`} onClick={() => setTab('schedule')}>📊 Depreciation Schedule</button>
        <button className={`tab ${tab === 'payments' ? 'active' : ''}`} onClick={() => setTab('payments')}>💸 Outstanding Payments</button>
      </div>

      {/* Edit Modal */}
      {editAsset && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditAsset(null)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-title">{editAsset.id ? 'Edit Asset' : 'Add New Asset'}</div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Asset Name *</label><input className="form-input" value={editAsset.name} onChange={e => setEditAsset(a => ({ ...a, name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Category *</label>
                <select className="form-select" value={editAsset.category} onChange={e => setEditAsset(a => ({ ...a, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Supplier</label><input className="form-input" value={editAsset.supplier || ''} onChange={e => setEditAsset(a => ({ ...a, supplier: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Invoice Number</label><input className="form-input" value={editAsset.invoice_number || ''} onChange={e => setEditAsset(a => ({ ...a, invoice_number: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Total Cost (UGX) *</label><input className="form-input" type="number" value={editAsset.total_cost} onChange={e => setEditAsset(a => ({ ...a, total_cost: parseFloat(e.target.value) || 0 }))} /></div>
              <div className="form-group"><label className="form-label">Amount Paid (UGX)</label><input className="form-input" type="number" value={editAsset.amount_paid} onChange={e => setEditAsset(a => ({ ...a, amount_paid: parseFloat(e.target.value) || 0 }))} /></div>
              <div className="form-group"><label className="form-label">Purchase Date</label><input className="form-input" type="date" value={editAsset.purchase_date || ''} onChange={e => setEditAsset(a => ({ ...a, purchase_date: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Useful Life (years)</label><input className="form-input" type="number" value={editAsset.useful_life_years} onChange={e => setEditAsset(a => ({ ...a, useful_life_years: parseInt(e.target.value) }))} /></div>
              <div className="form-group"><label className="form-label">Status</label>
                <select className="form-select" value={editAsset.status} onChange={e => setEditAsset(a => ({ ...a, status: e.target.value }))}>
                  <option>CWIP</option><option>Active</option><option>Prepayment</option><option>Disposed</option>
                </select>
              </div>
              {editAsset.status === 'Active' && (
                <div className="form-group"><label className="form-label">Commissioning Date</label><input className="form-input" type="date" value={editAsset.commissioning_date || ''} onChange={e => setEditAsset(a => ({ ...a, commissioning_date: e.target.value }))} /></div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" style={{ minHeight: 60 }} value={editAsset.description || ''} onChange={e => setEditAsset(a => ({ ...a, description: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight: 50 }} value={editAsset.notes || ''} onChange={e => setEditAsset(a => ({ ...a, notes: e.target.value }))} /></div>
            <div style={{ background: 'var(--amber-pale)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: '0.8rem' }}>
              <strong>Monthly depreciation:</strong> UGX {editAsset.total_cost && editAsset.useful_life_years ? Math.round(editAsset.total_cost / (editAsset.useful_life_years * 12)).toLocaleString() : '0'} / month
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setEditAsset(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveAsset} disabled={saving}>{saving ? <><span className="spinner" />Saving...</> : '💾 Save Asset'}</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'register' && (
        <div>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner dark" /></div>
          : assets.map(asset => {
            const dep = schedule.find(s => s.id === asset.id)
            const needsSignOff = !asset.sharon_confirmed || !asset.edward_authorised
            return (
              <div key={asset.id} className="card" style={{ marginBottom: 14, borderLeft: `4px solid ${asset.status === 'Active' ? 'var(--green-bright)' : asset.status === 'CWIP' ? 'var(--amber)' : 'var(--blue)'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.72rem', color: 'var(--text-muted)' }}>{asset.asset_number}</span>
                      <span className={`badge ${STATUS_COLORS[asset.status]}`}>{asset.status}</span>
                      {needsSignOff && <span className="badge badge-red">⚠️ Pending Sign-off</span>}
                    </div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--green-deep)' }}>{asset.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{asset.supplier} {asset.invoice_number ? `· Invoice ${asset.invoice_number}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.3rem', fontWeight: 700, color: 'var(--green-deep)' }}>UGX {(asset.total_cost || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>at cost · {asset.useful_life_years} yr life</div>
                  </div>
                </div>

                <div className="grid-2" style={{ marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 6 }}>Payment Status</div>
                    <div style={{ display: 'flex', gap: 16, fontSize: '0.82rem' }}>
                      <div><span style={{ color: 'var(--green-mid)', fontWeight: 700 }}>Paid: UGX {(asset.amount_paid || 0).toLocaleString()}</span></div>
                      {asset.amount_outstanding > 0 && <div><span style={{ color: 'var(--red)', fontWeight: 700 }}>Owed: UGX {(asset.amount_outstanding || 0).toLocaleString()}</span></div>}
                    </div>
                  </div>
                  {dep && asset.status === 'Active' && (
                    <div>
                      <div style={{ fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 6 }}>Depreciation</div>
                      <div style={{ fontSize: '0.82rem' }}>
                        <span>Monthly: UGX {Math.round(asset.monthly_depreciation || 0).toLocaleString()}</span>
                        <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                        <span>NBV: UGX {Math.round(dep.net_book_value || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                  {asset.status === 'CWIP' && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--amber)' }}>
                      ⏳ In progress — depreciation begins on commissioning
                    </div>
                  )}
                </div>

                {/* Sign-off section */}
                <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>SIGN-OFF:</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                    <span>{asset.sharon_confirmed ? '✅' : '⬜'}</span>
                    <span style={{ fontWeight: 600 }}>Sharon — Physical Receipt</span>
                    {!asset.sharon_confirmed && isSharonProfile && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleSignOff(asset.id, 'sharon')}>Sign Off</button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                    <span>{asset.edward_authorised ? '✅' : '⬜'}</span>
                    <span style={{ fontWeight: 600 }}>Edward — Capitalisation</span>
                    {!asset.edward_authorised && isEdwardProfile && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleSignOff(asset.id, 'edward')}>Authorise</button>
                    )}
                  </div>
                </div>

                {/* Documents */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {asset.invoice_url
                    ? <a href={asset.invoice_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">📄 View Invoice</a>
                    : isAdmin && (
                      <label style={{ cursor: 'pointer' }}>
                        <input type="file" accept="image/*,application/pdf" onChange={e => handleUpload(asset.id, 'invoice_url', e.target.files[0])} style={{ display: 'none' }} />
                        <span className="btn btn-ghost btn-sm">{uploading ? '⏳' : '📄 Upload Invoice'}</span>
                      </label>
                    )}
                  {asset.delivery_note_url
                    ? <a href={asset.delivery_note_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">📦 View Delivery Note</a>
                    : isAdmin && (
                      <label style={{ cursor: 'pointer' }}>
                        <input type="file" accept="image/*,application/pdf" onChange={e => handleUpload(asset.id, 'delivery_note_url', e.target.files[0])} style={{ display: 'none' }} />
                        <span className="btn btn-ghost btn-sm">📦 Upload Delivery Note</span>
                      </label>
                    )}
                  {isAdmin && asset.status === 'CWIP' && asset.sharon_confirmed && asset.edward_authorised && (
                    <button className="btn btn-primary btn-sm" onClick={() => handleCommission(asset.id)}>✅ Commission Asset</button>
                  )}
                  {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => setEditAsset(asset)}>✏️ Edit</button>}
                  {isAdmin && (asset.amount_outstanding || 0) > 0 && (
                    <button className="btn btn-primary btn-sm" onClick={() => { setPayModal(asset); setPayForm({ amount: '', payment_date: ugandaDate(), reference: '', notes: '' }) }}>
                      💳 Record Payment
                    </button>
                  )}
                </div>

                {asset.notes && <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', borderTop: '1px solid var(--border)', paddingTop: 8 }}>{asset.notes}</div>}
              </div>
            )
          })}
        </div>
      )}

      {tab === 'schedule' && (
        <div className="card">
          <div className="card-title">Depreciation Schedule</div>
          <div className="card-subtitle">Straight-line depreciation for all active assets</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Asset</th><th>Cost</th><th>Monthly Dep.</th><th>Months</th><th>Accum. Dep.</th><th>Net Book Value</th><th>Fully Dep. By</th></tr>
              </thead>
              <tbody>
                {schedule.filter(s => s.status === 'Active').map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>UGX {(s.total_cost || 0).toLocaleString()}</td>
                    <td style={{ color: 'var(--amber)' }}>UGX {Math.round(s.monthly_depreciation || 0).toLocaleString()}</td>
                    <td>{Math.round(s.months_depreciated || 0)}</td>
                    <td style={{ color: 'var(--red)' }}>UGX {Math.round(s.accumulated_depreciation || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700, color: 'var(--green-mid)' }}>UGX {Math.round(s.net_book_value || 0).toLocaleString()}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.fully_depreciated_date ? new Date(s.fully_depreciated_date).toLocaleDateString('en-UG', { month: 'short', year: 'numeric' }) : '—'}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                  <td>TOTAL</td>
                  <td>UGX {totalCost.toLocaleString()}</td>
                  <td style={{ color: 'var(--amber)' }}>UGX {Math.round(totalMonthlyDep).toLocaleString()}</td>
                  <td>—</td>
                  <td style={{ color: 'var(--red)' }}>UGX {Math.round(totalAccumDep).toLocaleString()}</td>
                  <td style={{ color: 'var(--green-mid)' }}>UGX {Math.round(totalNBV).toLocaleString()}</td>
                  <td>—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="alert alert-amber" style={{ marginTop: 14 }}>
            CWIP and Prepayment assets are not yet depreciating. They will begin depreciating from their commissioning date.
          </div>
        </div>
      )}

      {tab === 'payments' && (
        <div className="card">
          <div className="card-title">Outstanding Asset Payments</div>
          <div className="card-subtitle">Amounts still owed to suppliers for capital assets</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Asset</th><th>Supplier</th><th>Total Cost</th><th>Paid</th><th>Outstanding</th><th>Due When</th></tr></thead>
              <tbody>
                {assets.filter(a => a.amount_outstanding > 0).map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600 }}>{a.name}</td>
                    <td style={{ fontSize: '0.78rem' }}>{a.supplier}</td>
                    <td>UGX {(a.total_cost || 0).toLocaleString()}</td>
                    <td style={{ color: 'var(--green-mid)' }}>UGX {(a.amount_paid || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700, color: 'var(--red)' }}>UGX {(a.amount_outstanding || 0).toLocaleString()}</td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {a.asset_number === 'FA-001' ? 'Due now — completion payment' : 'On commissioning/handover'}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--red-pale)', fontWeight: 700 }}>
                  <td colSpan={4}>TOTAL OUTSTANDING</td>
                  <td style={{ color: 'var(--red)', fontSize: '1rem', fontFamily: "'Cormorant Garamond',serif" }}>
                    UGX {totalOutstanding.toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {commissionModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setCommissionModal(null)}>
          <div className="modal">
            <div className="modal-title">✅ Commission Asset</div>
            <div className="modal-subtitle">Set the commissioning date — depreciation will begin from this date.</div>
            <div className="form-group">
              <label className="form-label">Commissioning Date</label>
              <input className="form-input" type="date" value={commissionDate} onChange={e=>setCommissionDate(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setCommissionModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmCommission}>✅ Commission Asset</button>
            </div>
          </div>
        </div>
      )}
      {/* PAYMENT MODAL */}
      {payModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPayModal(null)}>
          <div className="modal">
            <div className="modal-title">💳 Record Payment — {payModal.name}</div>
            <div className="modal-subtitle">
              Total cost: UGX {(payModal.total_cost || 0).toLocaleString()} ·
              Already paid: UGX {(payModal.amount_paid || 0).toLocaleString()} ·
              <span style={{ color: 'var(--red)', fontWeight: 700 }}> Still owed: UGX {(payModal.amount_outstanding || 0).toLocaleString()}</span>
            </div>
            <div className="form-group">
              <label className="form-label">Amount Being Paid Now (UGX) *</label>
              <input className="form-input" type="number" value={payForm.amount}
                onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={`Max: ${(payModal.amount_outstanding || 0).toLocaleString()}`} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Payment Date</label>
              <input className="form-input" type="date" value={payForm.payment_date}
                onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Bank Reference</label>
              <input className="form-input" value={payForm.reference}
                onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))}
                placeholder="e.g. Bank transfer ref" />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-input" value={payForm.notes}
                onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional" />
            </div>
            {payForm.amount > 0 && (
              <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: '0.82rem' }}>
                <div>Remaining after this payment: UGX {Math.max(0, (payModal.amount_outstanding || 0) - parseFloat(payForm.amount || 0)).toLocaleString()}</div>
                {parseFloat(payForm.amount) >= (payModal.amount_outstanding || 0) && (
                  <div style={{ color: 'var(--green-mid)', fontWeight: 700, marginTop: 4 }}>✅ This will fully settle the balance</div>
                )}
              </div>
            )}
            <div className="alert alert-green" style={{ fontSize: '0.78rem' }}>
              This will: update the asset record · create a cash log entry · reduce Supplier Payables · post a journal entry (Dr Payables / Cr Cash)
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleRecordPayment}
                disabled={saving || !payForm.amount || parseFloat(payForm.amount) <= 0}>
                {saving ? <><span className="spinner" />Recording...</> : '💳 Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
